import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, Package, Calendar, RefreshCw, Trash2 } from "lucide-react";
import Header from "../components/Header";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(sessionStorage.getItem("sc_admin_token") || "");
  const [password, setPassword] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const login = async (e) => {
    e?.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("sc_admin_token", data.token);
      setToken(data.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("sc_admin_token");
    setToken("");
    setRequests([]);
  };

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/requests`, {
        headers: { "x-admin-token": token },
      });
      setRequests(data);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Could not load requests");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/requests/${id}/status`,
        { payment_status: status },
        { headers: { "x-admin-token": token } }
      );
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, payment_status: status } : r)));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo ordine?")) return;
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { headers: { "x-admin-token": token } });
      setRequests((rs) => rs.filter((r) => r.id !== id));
      toast.success("Ordine eliminato");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchRequests();
    const id = setInterval(fetchRequests, 30000);
    return () => clearInterval(id);
  }, [token, fetchRequests]);

  if (!token) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#B8C5CD" }}>
        <Toaster richColors position="top-center" />
        <Header />
        <div className="max-w-md mx-auto px-6 py-24">
          <div className="eyebrow mb-3">Admin</div>
          <h1 className="font-serif-display text-4xl mb-6" style={{ color: "#2C2A28" }}>Santa Chiara Dashboard</h1>
          <form onSubmit={login} className="bg-white rounded-2xl p-8 border" style={{ borderColor: "rgba(184,153,104,0.2)" }}>
            <label className="block text-sm font-medium mb-2" style={{ color: "#4A4744" }}>Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border rounded-xl px-4 py-3 text-sm outline-none focus:ring-2"
              style={{ borderColor: "rgba(184,153,104,0.3)", color: "#2C2A28" }}
            />
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="mt-5 w-full px-6 py-3 rounded-full font-medium transition"
              style={{ backgroundColor: "#2C2A28", color: "#B8C5CD" }}
            >
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#B8C5CD" }}>
      <Toaster richColors position="top-center" />
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="eyebrow">Admin</div>
            <h1 className="font-serif-display text-4xl tracking-tight" style={{ color: "#2C2A28" }}>Custom Gelato Requests</h1>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm" style={{ color: "#75716C" }}>
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="flex justify-end mb-6">
          <button
            data-testid="admin-refresh-btn"
            onClick={fetchRequests}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition"
            style={{ backgroundColor: "#2C2A28", color: "#F0E6D2", opacity: loading ? 0.6 : 1 }}
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Aggiorna ordini
          </button>
        </div>

        {loading && <p style={{ color: "#75716C" }}>Loading…</p>}
        {!loading && requests.length === 0 && (
          <div className="text-center py-24 bg-white rounded-3xl border" style={{ borderColor: "rgba(184,153,104,0.2)" }}>
            <Package size={36} strokeWidth={1.3} className="mx-auto mb-4" style={{ color: "#B89968" }} />
            <p style={{ color: "#4A4744" }}>No requests yet.</p>
          </div>
        )}

        <div className="grid gap-4">
          {requests.map((r) => (
            <div key={r.id} data-testid={`request-${r.id}`} className="bg-white rounded-2xl p-6 border transition hover:shadow-lg" style={{ borderColor: "rgba(184,153,104,0.2)" }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-serif-display text-2xl" style={{ color: "#2C2A28" }}>{r.gelato_name}</h3>
                    <StatusBadge status={r.payment_status} />
                    <span className="text-sm" style={{ color: "#B89968" }}>{r.quantity_kg} kg</span>
                    {r.price_usd ? <span className="text-sm font-bold" style={{ color: "#2C2A28" }}>${r.price_usd}</span> : null}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm" style={{ color: "#4A4744" }}>
                    <span>{r.customer.full_name}</span>
                    <span className="inline-flex items-center gap-1"><Mail size={12} />{r.customer.email}</span>
                    <span className="inline-flex items-center gap-1"><Phone size={12} />{r.customer.phone}</span>
                    <span className="inline-flex items-center gap-1"><Calendar size={12} />{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`view-${r.id}`}
                    onClick={() => setSelected(r)}
                    className="text-sm px-4 py-2 rounded-full border transition"
                    style={{ borderColor: "rgba(184,153,104,0.4)", color: "#2C2A28" }}
                  >
                    View
                  </button>
                  <select
                    data-testid={`status-${r.id}`}
                    value={r.payment_status}
                    onChange={(e) => updateStatus(r.id, e.target.value)}
                    className="text-sm px-3 py-2 rounded-full border bg-white"
                    style={{ borderColor: "rgba(184,153,104,0.4)", color: "#2C2A28" }}
                  >
                    <option value="pending">Pending</option>
                    <option value="link_sent">Link sent</option>
                    <option value="paid">Paid</option>
                    <option value="shipped">Shipped</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <button
                    data-testid={`delete-${r.id}`}
                    onClick={() => deleteRequest(r.id)}
                    className="text-sm p-2 rounded-full border transition"
                    style={{ borderColor: "rgba(143,90,90,0.4)", color: "#8F5A5A" }}
                    aria-label="Elimina ordine"
                    title="Elimina ordine"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && <RequestDetail request={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "#C5D0D6", color: "#B89968", label: "Pending" },
    link_sent: { bg: "#E8EEF3", color: "#5A7A8F", label: "Link sent" },
    paid: { bg: "#E7EFE0", color: "#5C7E48", label: "Paid" },
    shipped: { bg: "#A8BCC8", color: "#2C2A28", label: "Shipped" },
    completed: { bg: "#9CAF88", color: "#B8C5CD", label: "Completed" },
    cancelled: { bg: "#EFE0E0", color: "#8F5A5A", label: "Cancelled" },
  };
  const s = map[status] || map.pending;
  return (
    <span className="text-xs px-3 py-1 rounded-full font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function RequestDetail({ request, onClose }) {
  const r = request;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="detail-modal">
      <div className="absolute inset-0 backdrop-blur-md" style={{ backgroundColor: "rgba(44,42,40,0.45)" }} onClick={onClose} />
      <div className="relative z-10 max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-3xl p-8 border shadow-2xl" style={{ backgroundColor: "#B8C5CD", borderColor: "rgba(184,153,104,0.3)" }}>
        <div className="eyebrow mb-1">{r.id}</div>
        <h2 className="font-serif-display text-4xl tracking-tight" style={{ color: "#2C2A28" }}>{r.gelato_name}</h2>
        <div className="mt-4 text-sm" style={{ color: "#75716C" }}>Created {new Date(r.created_at).toLocaleString()}</div>

        <Section title="Customer">
          <Row label="Name" value={r.customer.full_name} />
          <Row label="Email" value={r.customer.email} />
          <Row label="Phone" value={r.customer.phone} />
          <Row label="Fulfillment" value={r.customer.fulfillment} />
          {r.customer.fulfillment === "delivery" ? (
            <Row label="Address" value={r.customer.shipping_address} />
          ) : (
            <Row label="Pickup" value={r.customer.pickup_location} />
          )}
          {r.customer.fulfillment === "delivery" && (
            <>
              <Row label="City" value={r.customer.shipping_city || "—"} />
              <Row label="State" value={r.customer.shipping_state || "—"} />
              <Row label="ZIP" value={r.customer.shipping_zip || "—"} />
            </>
          )}
          <Row label="Notes" value={r.customer.notes || "—"} />
        </Section>

        <Section title="Creation">
          <Row label="Quantity" value={`${r.quantity_kg} kg`} />
          <Row label="Gelato price" value={`$${r.price_usd || 0}`} />
          {r.customer.fulfillment === "delivery" && (
            <>
              <Row label="Shipping" value={`$${r.shipping_cost || 0}`} />
              <Row label="Total" value={`$${(r.price_usd || 0) + (r.shipping_cost || 0)}`} />
            </>
          )}
          <Row label="Style" value={r.styles.join(", ") || "—"} />
          <Row label="Fruits" value={r.fruits.join(", ") || "—"} />
          <Row label="Creams" value={r.creams.join(", ") || "—"} />
          <Row label="Something Crazy" value={(r.crazy || []).join(", ") || "—"} />
          <Row label="Crunchy" value={r.crunchy.join(", ") || "—"} />
          <Row label="Swirls" value={r.swirls.join(", ") || "—"} />
          <Row label="Alcohol" value={r.alcohol_type ? `${r.alcohol_type} · ${r.alcohol_percent}%` : "—"} />
          <Row label="Functional" value={r.functional.join(", ") || "—"} />
        </Section>

        <div className="mt-8 text-right">
          <button data-testid="close-detail-btn" onClick={onClose} className="px-6 py-2.5 rounded-full font-medium" style={{ backgroundColor: "#2C2A28", color: "#B8C5CD" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-7 pt-5 border-t" style={{ borderColor: "rgba(184,153,104,0.2)" }}>
    <div className="eyebrow mb-3">{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex gap-4 text-sm">
    <div className="w-28 flex-shrink-0" style={{ color: "#75716C" }}>{label}</div>
    <div style={{ color: "#2C2A28" }}>{value}</div>
  </div>
);
